import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'react-bootstrap'
import {
  Col,
  FormGroup,
  Form,
  FormText,
  CardBody,
  Row,
  Button,
  Card,
  CardHeader, Input
} from "reactstrap";
import SetTitle from '../../utils/set_title'

import { api } from '../../utils/index'
import { ToastContainer } from 'react-toastify'
import { notify } from '../../utils/notify'
import FileInput from '../../components/molecules/FileInput'
import { useFileUpload } from '../../hooks'
import CharCheckModal from "../../components/molecules/CharCheckModal";
import GPTEstimateModal from "../../components/molecules/GPTEstimateModal";
import Header from "../../components/Headers/Header";
import Select from "react-select";
import { DEFAULT_QUESTION_PROMPT } from "./defaultPrompt";

const LLMVisibilityForm = () => {
  SetTitle('LLM Visibility Report')
  const { id } = useParams()
  const [name, setName] = useState('')
  const [brand, setBrand] = useState('')
  const [country, setCountry] = useState('')
  const [file, setFile] = useState(null)
  const [useGpt, setUseGpt] = useState(false)
  const [useGemini, setUseGemini] = useState(false)
  const [usePerplexity, setUsePerplexity] = useState(false)
  const [customPrompt, setCustomPrompt] = useState(DEFAULT_QUESTION_PROMPT)
  const [taggingToolId, setTaggingToolId] = useState('')
  const [reportStatus, setReportStatus] = useState('')
  const [taggingToolReports, setTaggingToolReports] = useState([])
  const [isCreatingViews, setIsCreatingViews] = useState(false)
  const [isResuming, setIsResuming] = useState(false)

  const { upload, loading } = useFileUpload()

  const pendingSubmitRef = useRef({ forceRerun: null, questionsOnly: false })

  // Modal check caratteri
  const [showCharCheck, setShowCharCheck] = useState(false)
  const [errCharList, setErrCharList] = useState([])
  const [acceptedCharCheck, setAcceptedCharCheck] = useState(false)
  // Modal check gpt
  const [showGptCheck, setShowGptCheck] = useState(false)
  const [gptEstimateParams, setGptEstimateParams] = useState({
    n_keywords: -1,
    total_tokens: -1,
    estimate: -1,
  })
  const [acceptedGptCheck, setAcceptedGptCheck] = useState(false)
  const estimateSubmitInFlightRef = useRef(false)

  let history = useHistory()

  const handleSubmit = async (event, forceRerun = null, questionsOnly = false) => {
    if (event)
      event.preventDefault()

    if (!acceptedGptCheck) {
      pendingSubmitRef.current = { forceRerun, questionsOnly }
    }

    if (!name || (!file && !id)) {
      notify('Inserisci il nome dell\'analisi e carica un file.', 'error')
      return
    }
    if (!questionsOnly && !useGpt && !useGemini && !usePerplexity) {
      notify('Seleziona almeno un modello LLM (GPT, Gemini o Perplexity).', 'error')
      return
    }

    let formData = new FormData();
    formData.append('name', name)
    formData.append('brand', brand)
    formData.append('country', country)
    formData.append('file', file)
    formData.append('gpt', useGpt)
    formData.append('gemini', useGemini)
    formData.append('perplexity', usePerplexity)
    formData.append('custom_prompt', customPrompt)
    if (questionsOnly) {
      formData.append('questions_only', true)
    }
    if (taggingToolId) {
      formData.append('connected_tagging', taggingToolId)
    }
    if (!questionsOnly) {
      formData.append('gpt_estimate_accepted', acceptedGptCheck)
    }
    if (id && forceRerun !== null) {
      formData.append('force_rerun', forceRerun)
    }

    try {
      await upload(id ? 'put' : 'post', `llm-visibility/${id ? id + '/' : ''}`, formData, {
        onSuccess: (data) => {
          if (typeof data !== 'undefined') {
            notify("Richiesta mandata. Riceverai una mail al termine dell'elaborazione", 'success')
            setAcceptedCharCheck(false)
            setAcceptedGptCheck(false)
            setFile(null)
            setTimeout(() => history.push('llm-visibility'), 1000)
          } else {
            notify(
              'Richiesta fallita. Qualcosa è andato storto, riprovare o contattare un Innovation',
              'error'
            )
          }
        },
        onError: (errorMessage, err) => {
          if (err.response?.data?.message?.includes('ERROR Encoding keywords') && !id) {
            setErrCharList(err.response.data?.errors)
            setShowCharCheck(true)
          } else if (err.response?.data?.message?.includes('GPT Estimate')) {
            setGptEstimateParams(err.response.data?.estimates)
            setShowGptCheck(true)
          } else {
            notify(err.response.data?.error, 'warning')
            setAcceptedCharCheck(false)
            setAcceptedGptCheck(false)
          }
        },
      })
    } catch (err) {
      console.error('LLM Visibility submission failed:', err)
      //notify('An unexpected error occurred during submission.', 'error')
    }
  }

  const handleCreateViews = async () => {
    if (!id || !taggingToolId) {
      notify("Please save the report with a connected tagging tool first.", "warning");
      return;
    }
    setIsCreatingViews(true);
    try {
      const response = await api.post(`llm-visibility/${id}/create_tagging_views/`, { connected_tagging: taggingToolId });
      notify(response.data.message || "Successfully triggered view creation.", "success");
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Failed to create views.";
      notify(errorMessage, "error");
      console.error("Failed to create tagging views:", err);
    } finally {
      setIsCreatingViews(false);
    }
  };

  const handleResumeFromInterruption = async () => {
    if (!id) return
    setIsResuming(true)
    try {
      const response = await api.post(`llm-visibility/${id}/resume_from_interruption/`)
      notify(response.data?.message || "Ripresa avviata correttamente.", "success")
    } catch (err) {
      notify(err.response?.data?.error || "Impossibile riprendere l'elaborazione.", "error")
    } finally {
      setIsResuming(false)
    }
  }

  const handleRegenerateQuestions = async () => {
    if (!id) return
    try {
      const response = await api.post(`llm-visibility/${id}/regenerate_questions/`)
      notify(response.data?.message || "Rigenerazione domande avviata.", "success")
      setTimeout(() => history.push('llm-visibility'), 1000)
    } catch (err) {
      notify(err.response?.data?.error || "Impossibile rigenerare le domande.", "error")
    }
  }

  const resetForm = () => {
    setName('')
    setBrand('')
    setCountry('')
    setFile(null)
    setUseGpt(false)
    setUseGemini(false)
    setUsePerplexity(false)
    setCustomPrompt(DEFAULT_QUESTION_PROMPT)
    setTaggingToolId('')
    // This is a trick to reset the FileInput component visually
    document.getElementById("llm-visibility-form").reset();
  }

  useEffect(() => {
    // Fetch available tagging tool reports to populate the dropdown
    api
      .get('tagging-tool/reports/')
      .then((res) => {
        // Assuming the API returns a list of reports with 'id' and 'name'
        setTaggingToolReports(res.data || [])
      })
      .catch((err) => {
        console.error("Failed to fetch tagging tool reports:", err)
        notify("Could not load Tagging Tool reports.", 'warning')
      })

    if (id) {
      api.get(`llm-visibility/${id}/`)
        .then(res => {
          const report = res.data
          setName(report.name)
          setBrand(report.brand || '')
          setCountry(report.country || '')
          setUseGpt(report.gpt)
          setUseGemini(report.gemini)
          setUsePerplexity(report.perplexity || false)
          setCustomPrompt(report.custom_prompt || DEFAULT_QUESTION_PROMPT)
          setReportStatus(report.status)
          if (report.connected_tagging) {
            setTaggingToolId(report.connected_tagging)
          }
        })
        .catch(err => {
          notify('Failed to load existing report data.', 'error')
        })
    }
  }, [])

  useEffect(() => {
    if (acceptedGptCheck && !estimateSubmitInFlightRef.current) {
      estimateSubmitInFlightRef.current = true
      const { forceRerun, questionsOnly } = pendingSubmitRef.current
      const submitPromise = handleSubmit(null, forceRerun, questionsOnly)
      if (submitPromise && typeof submitPromise.finally === 'function') {
        submitPromise.finally(() => {
          setAcceptedGptCheck(false)
          estimateSubmitInFlightRef.current = false
        })
      } else {
        setAcceptedGptCheck(false)
        estimateSubmitInFlightRef.current = false
      }
    }
  }, [acceptedGptCheck])

  const confirmEvaluation = (e) => {
    e.preventDefault()
    setShowCharCheck(false)
    setAcceptedCharCheck(true)
  }

  const cancelEvaluation = () => {
    setShowCharCheck(false)
    setAcceptedGptCheck(false)
  }

  const confirmEstimate = (e) => {
    e.preventDefault()
    setShowGptCheck(false)
    setAcceptedGptCheck(true)
  }

  const cancelEstimate = () => {
    setShowGptCheck(false)
    setShowCharCheck(false)
  }

  return (
    <Header
      iconClass="ni ni-align-left-2 text-white"
      title="LLM Visibility"
      description={"Dato un set di Keyword, genera delle domande per gli LLM e controlla quanto i brand vengono citati in esse"}
    >

      <div className="pb-5">
        <Card className="bg-secondary shadow">
          <CardHeader className="bg-white border-0">
            <Row className="align-items-center">
              <Col md={10}>
                <h1 className="mb-0">Report LLM Visibility</h1>
              </Col>
              <Col md={2} style={{ textAlign: "right" }}>
                <Button color="primary" onClick={() => history.push('/llm-visibility')}>&larr; Back to List</Button>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <CharCheckModal show={showCharCheck} onHide={cancelEvaluation} onSubmit={confirmEvaluation} errCharList={errCharList} withExclusion={true} />
            <GPTEstimateModal show={showGptCheck} onHide={cancelEstimate} onSubmit={confirmEstimate} params={gptEstimateParams} />
            {loading ? (
              <div className="text-center">
                <Spinner animation="border" role="status">
                  <span className="sr-only">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <Form onSubmit={handleSubmit} id="llm-visibility-form" action="">
                <h6 className="heading-small text-muted mb-4">Configurazione dell'analisi</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="name" className="form-control-label">
                        Nome dell'analisi
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        placeholder="es. il nome del brand"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                      <FormText color="muted">
                        Il dataset BigQuery sarà nominato con il nome inserito + "_LLM".<br />
                        {name ? (
                          <span> Anteprima: <strong>{name}_LLM</strong></span>
                        ) : null}
                      </FormText>
                    </FormGroup>
                    <FormGroup>
                      <label htmlFor="brand" className="form-control-label">
                        Brand
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        placeholder="es. il nome del brand"
                        id="brand"
                        value={brand}
                        onChange={(e) => setBrand(e.target.value)}
                      />
                    </FormGroup>
                    <FormGroup>
                      <label htmlFor="country" className="form-control-label">
                        Country
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="select"
                        id="country"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                      >
                        <option value="">Non forzare la country</option>
                        <option value="IT">IT</option>
                        <option value="GB">GB</option>
                        <option value="US">US</option>
                        <option value="FR">FR</option>
                        <option value="ES">ES</option>
                      </Input>
                      <FormText color="muted">
                        La country viene applicata solo alle risposte GPT (web search user_location).
                      </FormText>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FileInput
                      onFileChange={setFile}
                      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      helpText={id
                        ? <>Carica un nuovo file Excel per sostituire le keyword. <br /> <b>Lascia vuoto per rieseguire con le keyword originali.</b></>
                        : <>Carica un file Excel contenente le tue keyword. <br /> <b>E' richiesta una colonna chiamata: "Keyword"</b></>
                      }
                      required={!id}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroup>
                      <label htmlFor="custom-prompt" className="form-control-label">
                        Custom Prompt (Opzionale)
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="textarea"
                        id="custom-prompt"
                        rows="4"
                        placeholder="Sovrascrivi il prompt di default per la generazione delle domande. Usa {keyword} come placeholder."
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                      />
                      <FormText color="muted">
                        Lascia vuoto per usare il prompt di default. Se inserito, deve contenere la variabile {'{keyword}'}.
                      </FormText>
                    </FormGroup>
                  </Col>
                </Row>
                <hr className="my-4" />
                <h6 className="heading-small text-muted mb-4">LLM & Collegamenti</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <label className="form-control-label">
                        LLM da usare
                      </label>
                      <div>
                        <div className="custom-control custom-checkbox custom-control-inline">
                          <Input
                            className="custom-control-input"
                            id="gpt-checkbox"
                            type="checkbox"
                            checked={useGpt}
                            onChange={() => setUseGpt(!useGpt)}
                          />
                          <label className="custom-control-label" htmlFor="gpt-checkbox">
                            OpenAI GPT
                          </label>
                        </div>
                        <div className="custom-control custom-checkbox custom-control-inline">
                          <Input
                            className="custom-control-input"
                            id="gemini-checkbox"
                            type="checkbox"
                            checked={useGemini}
                            onChange={() => setUseGemini(!useGemini)}
                          />
                          <label className="custom-control-label" htmlFor="gemini-checkbox">
                            Google Gemini
                          </label>
                        </div>
                        <div className="custom-control custom-checkbox custom-control-inline">
                          <Input
                            className="custom-control-input"
                            id="perplexity-checkbox"
                            type="checkbox"
                            checked={usePerplexity}
                            onChange={() => setUsePerplexity(!usePerplexity)}
                          />
                          <label className="custom-control-label" htmlFor="perplexity-checkbox">
                            Perplexity
                          </label>
                        </div>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md={6} className="d-flex flex-column">
                    <label htmlFor="tagging-tool-select" className="form-control-label">
                      Connetti al Tagging Tool (Opzionale)
                    </label>
                    <div className="d-flex align-items-center">
                      <div style={{ flexGrow: 1 }}>
                        <Select
                          options={taggingToolReports.map((r) => {
                            return { value: r.id, label: r.name }
                          })}
                          searchable
                          isClearable
                          id="tagging-tool-select"
                          onChange={(e) => setTaggingToolId(e?.value || '')}
                          value={taggingToolId ? { value: taggingToolId, label: taggingToolReports.find(r => r.id === taggingToolId)?.name } : null}
                        />
                        <FormText color="muted">
                          Connetti un report del tagging tool esistente per importarne i tag.
                        </FormText>
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row className="mt-2 mb-5">
                  <Col className="text-right">
                    {id && (
                      <Button color="primary" size="lg" className="mr-3" onClick={(e) => handleSubmit(e, null, false)}>
                        Salva
                      </Button>
                    )}
                    {id && taggingToolId && (
                      <Button color="success" onClick={handleCreateViews} size="lg" title="Rigenera il collegamento con il Tagging Tool su BQ" disabled={isCreatingViews}>
                        {isCreatingViews ? (
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        ) : (
                          <>
                            <i className="fa fa-database" />
                          </>
                        )}
                      </Button>
                    )}
                    {id && reportStatus === 'EXPORTED_QUESTIONS' && (
                      <>
                        <Button color="secondary" size="lg" onClick={handleRegenerateQuestions} className="mr-3">
                          Rigenera Domande
                        </Button>
                        <Button color="info" size="lg" onClick={handleResumeFromInterruption}>
                          Esegui report completo
                        </Button>
                      </>
                    )}
                    {id && reportStatus === 'ERROR' && (
                      <Button color="warning" size="lg" onClick={handleResumeFromInterruption} className="mr-3" disabled={isResuming}>
                        {isResuming ? "Ripresa in corso..." : "Riprendi dall'interruzione"}
                      </Button>
                    )}
                    {!id && (
                      <>
                        <Button color="secondary" size="lg" className="mr-3" onClick={(e) => handleSubmit(e, null, true)}>
                          Genera Domande
                        </Button>
                        <Button color="info" size="lg" onClick={(e) => handleSubmit(e, id ? true : null)}>
                          Crea il report
                        </Button>
                      </>
                    )}
                  </Col>
                </Row>
              </Form>
            )}
            <ToastContainer />
          </CardBody>
        </Card>
      </div>
    </Header>
  )
}
export default LLMVisibilityForm
